<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useConfirm } from 'primevue/useconfirm'

// PrimeVue Components
import { default as PProgressSpinner } from 'primevue/progressspinner'
import { default as PMessage } from 'primevue/message'
import { default as PButton } from 'primevue/button'

// Custom Components
import CQuizHeader from '@/presentation/components/quiz/CQuizHeader.vue'
import CQuizVersionSelector from '@/presentation/components/quiz/CQuizVersionSelector.vue'
import CQuizQuestionList from '@/presentation/components/quiz/CQuizQuestionList.vue'
import CQuizQuestionAnswer from '@/presentation/components/quiz/CQuizQuestionAnswer.vue'

// Stores
import { useQuizStore } from '@/presentation/stores/quiz/quiz.ts'
import { useLearningCoursesStore } from '@/presentation/stores/learning/learningCourses.ts'
import { useLearningModulesStore } from '@/presentation/stores/learning/learningModules.ts'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus.ts'

// Types
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import type { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'
import type { QuizQuestion } from '@/core/domain/quiz/QuizQuestion.ts'
import { useQuizAccessUtils } from '@/presentation/composables/quiz/useQuizAccessUtils.ts'

// Utils
const route = useRoute()
const router = useRouter()
const confirm = useConfirm()
const { canEditQuizVersion } = useQuizAccessUtils()

// Props from route
const props = defineProps<{
  courseId: number
  moduleId?: number
  grainId?: number
  taskId?: number
  quizId: number
  axis?: string
}>()

// Stores
const quizStore = useQuizStore()
const coursesStore = useLearningCoursesStore()
const modulesStore = useLearningModulesStore()

// Store refs
const { currentQuiz, loading } = storeToRefs(quizStore)
const { currentCourse } = storeToRefs(coursesStore)

// Local state
const isLoading = ref(true)
const selectedVersion = ref<QuizVersion | null>(null)
const originalVersion = ref<QuizVersion | null>(null) // Copy of original version for comparison
const displayedVersion = ref<QuizVersion | null>(null) // Version shown in the selector (for display only)
const currentDisplayedQuestionIndex = ref<number | null>(null)
const learningModule = ref<any>(null)
const learningGrain = ref<any>(null)

// Computed
const currentQuestion = computed(() => {
  if (currentDisplayedQuestionIndex.value === null || !selectedVersion.value?.questions) {
    return null
  }
  return selectedVersion.value.questions[currentDisplayedQuestionIndex.value] || null
})

const hasUnsavedChanges = computed(() => {
  if (!selectedVersion.value || !originalVersion.value) {
    return false
  }

  // Compare current version with original version
  return !areVersionsEqual(selectedVersion.value, originalVersion.value)
})

const backTo = computed(() => {
  if (props.moduleId) {
    return {
      name: 'learning-course-update-module',
      params: { courseId: props.courseId, moduleId: props.moduleId },
      query: { grainId: props.grainId }
    }
  } else if (props.taskId) {
    return { name: 'learning-course-update', params: { id: props.courseId } }
  }
  return { name: 'learning-courses-list' }
})

const task = computed(() => {
  if (props.taskId) {
    return currentCourse.value?.domain.tasks.find((task) => task.id === props.taskId) || null
  }
  return null
})

// Methods
function notifyStoresOfQuizUpdate(quiz: Quiz) {
  console.log('=== QUIZ UPDATE NOTIFICATION ===')
  console.log('Props context:', {
    taskId: props.taskId,
    grainId: props.grainId,
    quizId: props.quizId
  })
  console.log('Quiz to notify:', quiz)

  // Notifier les stores appropriés selon le contexte
  if (props.taskId) {
    // Quiz dans une tâche → notifier le store learningCourses
    console.log('🔔 Notifying learningCourses store of quiz update')
    console.log('Current course in store:', currentCourse.value)
    storeEventBus.emit('learning:quizdiag:updated', { quiz })
  } else if (props.grainId) {
    // Quiz dans un grain → notifier le store learningModules
    console.log('🔔 Notifying learningModules store of quiz update')
    storeEventBus.emit('learning:quizgrain:updated', { quiz })
  } else {
    console.log('⚠️ No taskId or grainId found - no store will be notified')
  }

  console.log('=== END NOTIFICATION ===')
}

async function loadQuizData() {
  try {
    console.log('Loading quiz data for:', props.quizId)

    // Fetch the quiz
    const quiz = await quizStore.fetchQuizById(props.quizId)

    // Determine which version to select
    let versionToSelect: QuizVersion | null = null

    // Check if version is specified in URL
    const versionIdFromUrl = route.query.version
    if (versionIdFromUrl && quiz) {
      const versionId = Number(versionIdFromUrl)
      versionToSelect = quiz.findVersionById(versionId)
    }

    // Fallback to active version if no version specified or version not found
    if (!versionToSelect && quiz) {
      versionToSelect = quiz.activeVersion
    }

    // Set both selected and displayed versions, create original copy
    selectedVersion.value = versionToSelect
    displayedVersion.value = versionToSelect
    originalVersion.value = createVersionCopy(versionToSelect)

    // Update URL to reflect selected version
    if (versionToSelect) {
      updateUrlVersion(versionToSelect.id)
    }

    console.log('Quiz loaded with version:', versionToSelect?.id)
  } catch (error) {
    console.error('Error loading quiz:', error)
  }
}

async function loadContextData() {
  try {
    // Load module and grain if needed
    if (props.moduleId) {
      const module = await modulesStore.fetchModuleById(props.moduleId, props.courseId)
      learningModule.value = module

      if (props.grainId && module?.learningGrains) {
        learningGrain.value =
          module.learningGrains.find((grain) => grain.id === props.grainId) || null
      }
    }
  } catch (error) {
    console.error('Error loading context data:', error)
  }
}

function updateUrlVersion(versionId: number) {
  // Only update URL if it's different from current
  if (route.query.version !== versionId.toString()) {
    router.replace({
      ...route,
      query: {
        ...route.query,
        version: versionId.toString()
      }
    })
  }
}

function handleVersionChange(newVersion: QuizVersion | null) {
  // Check for unsaved changes before changing version
  if (hasUnsavedChanges.value) {
    confirm.require({
      message:
        'Vous avez des modifications non sauvegardées. Voulez-vous les sauvegarder avant de changer de version ?',
      header: 'Modifications non sauvegardées',
      acceptLabel: 'Sauvegarder et continuer',
      rejectLabel: 'Abandonner les modifications',
      accept: async () => {
        try {
          await saveChanges()
          proceedWithVersionChange(newVersion)
        } catch (error) {
          console.error('Error saving changes:', error)
          // Reset the display to current version if save failed
          displayedVersion.value = selectedVersion.value
        }
      },
      reject: () => {
        // User chose to abandon changes
        proceedWithVersionChange(newVersion)
      }
    })
    return
  }

  // No unsaved changes, proceed directly
  proceedWithVersionChange(newVersion)
}

function proceedWithVersionChange(version: QuizVersion | null) {
  selectedVersion.value = version
  displayedVersion.value = version
  originalVersion.value = createVersionCopy(version)

  if (version) {
    updateUrlVersion(version.id)
    console.log('Version changed to:', version.id)
  }

  // Reset question selection when version changes
  currentDisplayedQuestionIndex.value = null
}

function handleVersionChangeNeeded(newVersion: QuizVersion) {
  // Mettre à jour la version sélectionnée
  handleVersionChange(newVersion)

  // Notifier les stores si nécessaire (après création d'une nouvelle version)
  if (currentQuiz.value) {
    notifyStoresOfQuizUpdate(currentQuiz.value)
  }
}

function handleVersionActivated(updatedQuiz: Quiz) {
  // Mettre à jour le quiz actuel dans le store local
  currentQuiz.value = updatedQuiz

  // Notifier les stores appropriés selon le contexte
  notifyStoresOfQuizUpdate(updatedQuiz)
}

function handleQuestionIndexChange(index: number) {
  // Check for unsaved changes before changing question
  if (hasUnsavedChanges.value) {
    confirm.require({
      message:
        'Vous avez des modifications non sauvegardées. Voulez-vous les sauvegarder avant de changer de question ?',
      header: 'Modifications non sauvegardées',
      acceptLabel: 'Sauvegarder et continuer',
      rejectLabel: 'Abandonner les modifications',
      accept: async () => {
        try {
          await saveChanges()
          currentDisplayedQuestionIndex.value = index
        } catch (error) {
          console.error('Error saving changes:', error)
          // Don't change question if save failed
        }
      },
      reject: () => {
        // User chose to abandon changes, reset to original and then change question
        resetToOriginalVersion()
        currentDisplayedQuestionIndex.value = index
      }
    })
    return
  }

  // No unsaved changes, proceed directly
  currentDisplayedQuestionIndex.value = index
}

function handleQuestionUpdate(updatedQuestion: QuizQuestion) {
  // Update the question in the selected version
  if (selectedVersion.value?.questions && currentDisplayedQuestionIndex.value !== null) {
    selectedVersion.value.questions[currentDisplayedQuestionIndex.value] = updatedQuestion
  }
}

async function saveChanges() {
  if (!selectedVersion.value) return

  try {
    console.log('Saving changes for version:', selectedVersion.value.id)
    const updatedQuiz = await quizStore.updateQuizVersion(props.quizId, selectedVersion.value)

    // Update the original version after successful save
    updateOriginalVersion()

    // Notifier les stores appropriés selon le contexte
    if (updatedQuiz) {
      notifyStoresOfQuizUpdate(updatedQuiz)
    }

    console.log('Changes saved successfully')
  } catch (error) {
    console.error('Error saving changes:', error)
  }
}

async function validateAndLeave() {
  if (hasUnsavedChanges.value) {
    confirm.require({
      message:
        'Vous avez des modifications non sauvegardées. Voulez-vous les sauvegarder avant de quitter ?',
      header: 'Modifications non sauvegardées',
      acceptLabel: 'Sauvegarder et quitter',
      rejectLabel: 'Quitter sans sauvegarder',
      accept: async () => {
        try {
          await saveChanges()
          await router.push(backTo.value)
        } catch (error) {
          console.error('Error saving changes:', error)
          // Don't navigate if save failed
        }
      },
      reject: async () => {
        await router.push(backTo.value)
      }
    })
  } else {
    await router.push(backTo.value)
  }
}

// Watch for URL changes to update selected version
watch(
  () => route.query.version,
  async (newVersionId) => {
    if (newVersionId && currentQuiz.value) {
      const versionId = Number(newVersionId)
      const version = currentQuiz.value.findVersionById(versionId)

      if (version && version.id !== selectedVersion.value?.id) {
        selectedVersion.value = version
        displayedVersion.value = version
        originalVersion.value = createVersionCopy(version)
        currentDisplayedQuestionIndex.value = null
        console.log('Version updated from URL:', versionId)
      }
    }
  }
)

// Route leave guard
onBeforeRouteLeave(async (to, from, next) => {
  if (hasUnsavedChanges.value) {
    confirm.require({
      message:
        'Vous avez des modifications non sauvegardées. Voulez-vous les sauvegarder avant de quitter ?',
      header: 'Modifications non sauvegardées',
      acceptLabel: 'Sauvegarder et quitter',
      rejectLabel: 'Quitter sans sauvegarder',
      accept: async () => {
        try {
          await saveChanges()
          next() // Proceed with navigation after saving
        } catch (error) {
          console.error('Error saving changes:', error)
          next(false) // Cancel navigation if save failed
        }
      },
      reject: () => {
        next() // Proceed with navigation without saving
      }
    })
  } else {
    next() // No unsaved changes, proceed directly
  }
})

// Initialize data on mount
onMounted(async () => {
  isLoading.value = true

  try {
    await Promise.all([loadQuizData(), loadContextData()])
  } finally {
    isLoading.value = false
  }
})

// Utility functions for change detection
function createVersionCopy(version: QuizVersion | null): QuizVersion | null {
  if (!version) return null

  // Deep clone the version to avoid reference issues
  return JSON.parse(JSON.stringify(version))
}

function areVersionsEqual(version1: QuizVersion, version2: QuizVersion): boolean {
  // Compare all relevant properties that can be modified
  if (version1.title !== version2.title) return false
  if (version1.comment !== version2.comment) return false
  if (version1.questions.length !== version2.questions.length) return false

  // Compare each question
  for (let i = 0; i < version1.questions.length; i++) {
    const q1 = version1.questions[i]
    const q2 = version2.questions[i]

    if (!areQuestionsEqual(q1, q2)) return false
  }

  return true
}

function areQuestionsEqual(question1: QuizQuestion, question2: QuizQuestion): boolean {
  // Compare question properties
  if (question1.label !== question2.label) return false
  if (question1.order !== question2.order) return false
  if (question1.type !== question2.type) return false
  if (question1.feedbacks.correct !== question2.feedbacks.correct) return false
  if (question1.feedbacks.wrong !== question2.feedbacks.wrong) return false
  if (question1.answers.length !== question2.answers.length) return false

  // Compare answers using JSON stringify for deep comparison
  return JSON.stringify(question1.answers) === JSON.stringify(question2.answers)
}

function updateOriginalVersion() {
  selectedVersion.value = currentQuiz.value?.findVersionById(selectedVersion.value?.id)
  originalVersion.value = createVersionCopy(selectedVersion.value)
}

function resetToOriginalVersion() {
  if (originalVersion.value) {
    selectedVersion.value = createVersionCopy(originalVersion.value)
  }
}
</script>

<template>
  <div v-if="isLoading" class="flex h-full w-full items-center justify-center">
    <p-progress-spinner />
  </div>

  <div v-else-if="currentQuiz && currentCourse" class="flex h-full flex-col">
    <!-- Header -->
    <c-quiz-header
      :back-to="backTo"
      :learning-module="learningModule"
      :learning-grain="learningGrain"
      :learning-course="currentCourse"
      :task="task"
      @save="validateAndLeave"
    />

    <!-- Unsaved Changes Indicator -->
    <div v-if="hasUnsavedChanges" class="mb-4 border-l-4 border-amber-400 bg-amber-50 p-4">
      <div class="flex items-center">
        <div class="ml-3">
          <p class="text-sm text-amber-800">
            <i class="pi pi-exclamation-triangle mr-2"></i>
            <strong>Modifications non sauvegardées</strong> - Vos modifications seront perdues si
            vous quittez sans sauvegarder.
          </p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Left Panel - Version Selector & Question List -->
      <div class="flex w-1/3 flex-col border-r border-gray-200 p-6">
        <!-- Version Selector -->
        <div class="mb-6">
          <div class="mb-2 flex items-center gap-2">
            <c-quiz-version-selector
              :quiz="currentQuiz"
              v-model:selected-version="displayedVersion"
              @update:selected-version="handleVersionChange"
              @version-activated="handleVersionActivated"
            />
            <i
              v-if="hasUnsavedChanges"
              class="pi pi-circle-fill text-xs text-amber-500"
              title="Changements non sauvegardés"
            />
          </div>
        </div>

        <!-- Question List -->
        <div class="flex-1 overflow-auto">
          <c-quiz-question-list
            v-if="selectedVersion"
            :quiz="currentQuiz"
            :selected-version="selectedVersion"
            :has-unsaved-changes="hasUnsavedChanges"
            v-model:questions="selectedVersion.questions"
            v-model:current-displayed-question-index="currentDisplayedQuestionIndex"
            @cancel:update="loadQuizData"
            @version-change-needed="handleVersionChangeNeeded"
          />
        </div>
      </div>

      <!-- Right Panel - Question Editor -->
      <div class="flex-1 overflow-auto p-6">
        <div v-if="currentQuestion" class="mb-4">
          <div class="mb-4 flex items-center gap-2">
            <h3 class="text-lg font-semibold">
              Question
              {{ currentDisplayedQuestionIndex !== null ? currentDisplayedQuestionIndex + 1 : '' }}
            </h3>
            <i
              v-if="hasUnsavedChanges"
              class="pi pi-circle-fill text-xs text-amber-500"
              title="Changements non sauvegardés"
            />
          </div>

          <c-quiz-question-answer
            v-model:question="currentQuestion"
            :validate-update="saveChanges"
            :can-modify-structure="
              (selectedVersion?.canModifyStructure && canEditQuizVersion(selectedVersion)) ?? false
            "
            :can-modify-content="
              (selectedVersion?.canModifyContent && canEditQuizVersion(selectedVersion)) ?? false
            "
            :quiz-type="currentQuiz?.type"
            @cancel="loadQuizData"
            @question-updated="handleQuestionUpdate"
          />
        </div>

        <p-message v-else severity="info" icon="pi pi-info-circle">
          Sélectionnez une question pour commencer l'édition.
        </p-message>
      </div>
    </div>
  </div>

  <!-- Error State -->
  <div v-else class="flex h-full w-full items-center justify-center">
    <p-message severity="error" icon="pi pi-exclamation-triangle">
      Erreur lors du chargement du quiz.
    </p-message>
  </div>
</template>

<style scoped>
.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}
</style>
